/**
 * Column of the logistic vehicles import table. `field` is a path in the validated row's `entity`
 * (LogisticsVehicle). The labels are the file headers, which match the vehicles export.
 */
export interface LogisticsVehiclesImportColumn {
  field: string;
  label: string;
  // The server stores the file's "Mileage In Km" as meters in vehicleProfile.meters.
  format?: 'metersToKm';
}

export interface LogisticsVehiclesImportConfig {
  label: string;
  entity: string;
  importerPath: string;
  permissions: string[];
  // Polling the batch status needs this one too; without it the 403 sends the user to the 403 page.
  statusPermission: string;
  schemaPermission: string;
  columns: LogisticsVehiclesImportColumn[];
}

/**
 * /importers-logistics-vehicles (LogisticsVehicleImporterController): validate and import need
 * vehicleInstance.logisticsVehicle.create, the batch status needs .list, and the schema endpoints
 * need import.schemas. `entity` is the SchemaEntityType of the mapping schemas.
 */
export const LOGISTICS_VEHICLES_IMPORT_CONFIG: LogisticsVehiclesImportConfig = {
  label: 'Logistic Vehicles',
  entity: 'LogisticsVehicle',
  importerPath: 'importers-logistics-vehicles',
  permissions: ['vehicleInstance.logisticsVehicle.create'],
  statusPermission: 'vehicleInstance.logisticsVehicle.list',
  schemaPermission: 'import.schemas',
  columns: [
    {field: 'id', label: 'ID'},
    {field: 'externalId', label: 'External ID'},
    {field: 'name', label: 'Name'},
    {field: 'licensePlateNumber', label: 'License Plate Number'},
    {field: 'vehicleIdentificationNumber', label: 'Vehicle Identification Number'},
    // The file may name the type; validate resolves it, so the row only carries the type id.
    {field: 'vehicleTypeId', label: 'Vehicle Type Id'},
    {field: 'ownershipType', label: 'Ownership Type'},
    {field: 'vehicleStatus', label: 'Vehicle Status'},
    {field: 'vehicleStatusChangeReason.name', label: 'Status Reason'},
    {field: 'make', label: 'Make'},
    {field: 'model', label: 'Model'},
    {field: 'vehicleProfile.meters', label: 'Mileage In Km', format: 'metersToKm'},
    {field: 'vehicleProfile.year', label: 'Year'},
    {field: 'vehicleProfile.modelYear', label: 'Model Year'},
    {field: 'lifecycleEstimates.fuelConsumption', label: 'Fuel Consumption'},
    {field: 'lifecycleEstimates.estimatedAnnualUsage', label: 'Estimated Annual Usage'},
    {field: 'weight', label: 'Weight'},
    {field: 'allowedWeight', label: 'Allowed Weight'},
    {field: 'branchIds', label: 'Branch Ids'},
    {field: 'insuranceDetails.purchasePrice', label: 'Purchase Price'},
    {field: 'insuranceDetails.insuranceAmountPerMonth', label: 'Insurance Amount Per Month'}
  ]
};
